import API, { getUserFromRedux } from '@edulastic/api/src/utils/API'
import { useEffect, useRef, useState } from 'react'
import mqtt from 'mqtt'

const api = new API()

const DEFAULT_MAX_RECONNECT_ATTEMPTS = 3
const DEFAULT_INITIAL_BACKOFF_MS = 1000
const DEFAULT_MAX_BACKOFF_MS = 60_000

export const getSignedUrl = async (isRetry = false) => {
  const currentLoggedInUser = getUserFromRedux()
  const userId = currentLoggedInUser?.user?._id
  const res = await api.callApi({
    url: `/realtime/url?user=${userId}`,
    headers: isRetry
      ? { 'Cache-Control': 'no-cache', Pragma: 'no-cache', Expires: '0' }
      : {},
  })
  return res?.data?.url || ''
}

const useRealtime = (topics, actionMap, options = {}) => {
  const {
    maxReconnectAttempts = DEFAULT_MAX_RECONNECT_ATTEMPTS,
    initialReconnectDelayMs = DEFAULT_INITIAL_BACKOFF_MS,
    maxReconnectDelayMs = DEFAULT_MAX_BACKOFF_MS,
    dynamicTopics,
    topicsWillBeAdded,
    ...mqttConnectOptions
  } = options

  const [mqttUrl, setMqttUrl] = useState('')
  const [retClient, setClient] = useState(null)

  const intentionalCloseRef = useRef(false)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptRef = useRef(0)
  const clientRef = useRef(null)

  const actionMapRef = useRef(actionMap)
  const topicsRef = useRef(topics)

  useEffect(() => {
    actionMapRef.current = actionMap
    topicsRef.current = topics
  }, [actionMap, topics])

  useEffect(() => {
    const fetchInitialUrl = async () => {
      try {
        const url = await getSignedUrl()
        if (url) setMqttUrl(url)
      } catch (err) {
        console.error('realtime: Failed to fetch initial URL', err)
      }
    }
    fetchInitialUrl()
  }, [])

  const deps = [
    mqttUrl,
    maxReconnectAttempts,
    initialReconnectDelayMs,
    maxReconnectDelayMs,
  ]

  useEffect(() => {
    if (!mqttUrl) return undefined

    intentionalCloseRef.current = false
    reconnectAttemptRef.current = 0
    let cancelled = false

    const clearReconnectTimer = () => {
      if (reconnectTimeoutRef.current != null) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }

    const connectOpts = {
      ...mqttConnectOptions,
      reconnectPeriod: 0,
    }

    // 1. Define attachClient FIRST as a const
    const attachClient = (url) => {
      if (cancelled || intentionalCloseRef.current) return

      if (clientRef.current) {
        try {
          clientRef.current.end(true)
        } catch (e) {
          console.warn('realtime: error cleaning up old client', e)
        }
      }

      const client = mqtt.connect(url, connectOpts)
      clientRef.current = client

      client.on('connect', () => {
        if (cancelled || intentionalCloseRef.current) return
        setClient(client)

        const currentTopics = topicsRef.current || []

        if (currentTopics.length > 0) {
          client.subscribe(currentTopics, (err, granted) => {
            if (err) {
              console.error(`realtime: error subscribing to topics`, err)
            } else {
              console.log(
                'realtime: connected and subscribed to',
                granted.map((g) => g.topic)
              )
              // Only reset the counter once subscriptions are securely acknowledged
              reconnectAttemptRef.current = 0
            }
          })
        } else {
          // If there are no topics to subscribe to, treat the connection as fully successful
          reconnectAttemptRef.current = 0
        }
      })

      client.on('message', (_topic, message) => {
        try {
          const msgObj = JSON.parse(message.toString())
          const type = msgObj.type || 'unknown'

          if (actionMapRef.current && actionMapRef.current[type]) {
            actionMapRef.current[type](msgObj.data)
          }
        } catch (err) {
          console.error('realtime: failed to parse/handle message', err)
        }
      })

      client.on('error', (err) => {
        console.error('error in mqtt client', err)
      })

      client.on('close', () => {
        if (intentionalCloseRef.current || cancelled) return
        setClient(null)
        // eslint-disable-next-line no-use-before-define
        runReconnect()
      })
    }

    // 2. Define runReconnect SECOND as a const
    const runReconnect = () => {
      if (cancelled || intentionalCloseRef.current) return

      clearReconnectTimer()

      if (reconnectAttemptRef.current >= maxReconnectAttempts) {
        console.warn(
          `realtime: stopped reconnecting after ${maxReconnectAttempts} attempt(s)`
        )
        return
      }

      const delay = Math.min(
        maxReconnectDelayMs,
        initialReconnectDelayMs * 2 ** reconnectAttemptRef.current
      )
      reconnectAttemptRef.current += 1

      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null
        if (cancelled || intentionalCloseRef.current) return

        getSignedUrl(true)
          .then((freshUrl) => {
            if (cancelled || intentionalCloseRef.current) return
            if (!freshUrl) {
              console.warn('realtime: empty signed URL on reconnect')
              runReconnect()
              return
            }
            attachClient(freshUrl)
          })
          .catch((err) => {
            console.error('realtime: getSignedUrl failed during reconnect', err)
            runReconnect()
          })
      }, delay)
    }

    attachClient(mqttUrl)

    return () => {
      cancelled = true
      intentionalCloseRef.current = true
      clearReconnectTimer()

      const c = clientRef.current
      clientRef.current = null

      if (c) {
        try {
          c.end(true)
        } catch (e) {
          console.warn('error ending realtime connection', e.message)
        }
      }
      setClient(null)
    }
  }, deps)

  return retClient
}

export default useRealtime
